import { Component, Inject, ChangeDetectionStrategy, signal } from '@angular/core'
import { FieldTree, form, maxLength, required } from '@angular/forms/signals'
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog'
import { MatSnackBar } from '@angular/material/snack-bar'

import { DateService } from '@lemon/core/service/date.service'
import { FirebaseLemonator } from 'app/_angular/service/firebase-lemonator.service'
import { FormValidationService } from 'app/_jaettu-angular/service/form-validation.service'
import { Asiakas } from 'app/_jaettu-lemonator/model/asiakas'
import { KIRJAUSEHDOTUS_PALAUTE_MAX_PITUUS, KirjausehdotusPalauteLahetysPyynto } from '../../_jaettu-lemonator/model/llm-kirjausehdotus-palaute'
import { NaytettavaKirjaus } from '../kirjanpito.component'
import { lahetaKirjausehdotusPalaute } from './kirjaus.ai-ehdotus-ilmoita.client'

export interface KirjausAiEhdotusIlmoitaDialogData {
  naytettavaKirjaus: NaytettavaKirjaus
  asiakas: Asiakas
}

interface IlmoitusLomake {
  palaute: string
}

@Component({
  templateUrl: './kirjaus.ai-ehdotus-ilmoita.dialog.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class KirjausAiEhdotusIlmoitaDialog {
  lomake = form(signal<IlmoitusLomake>({ palaute: '' }), polku => {
    required(polku.palaute)
    maxLength(polku.palaute, KIRJAUSEHDOTUS_PALAUTE_MAX_PITUUS)
  })
  readonly lahetetaan = signal(false)
  maxPituus = KIRJAUSEHDOTUS_PALAUTE_MAX_PITUUS

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: KirjausAiEhdotusIlmoitaDialogData,
    private _dialogRef: MatDialogRef<KirjausAiEhdotusIlmoitaDialog, boolean>,
    private _firebase: FirebaseLemonator,
    private _formValidationService: FormValidationService,
    private _dateService: DateService,
    private _snackbar: MatSnackBar,
  ) {}

  onkoVirhe<T>(kentta: FieldTree<T>, laji: string): boolean {
    return kentta()
      .errors()
      .some(virhe => virhe.kind === laji)
  }

  async laheta() {
    if (this.lahetetaan()) {
      return
    }
    if (this.lomake().invalid()) {
      this._formValidationService.merkitseKokoLomakeKosketuksi(this.lomake)
      return
    }

    this.lahetetaan.set(true)
    try {
      const pyynto = this._muodostaPyynto(this.lomake.palaute().value().trim())
      const vastaus = await lahetaKirjausehdotusPalaute(this._firebase, pyynto)
      if (vastaus.e) {
        this._snackbar.open('Palautteen lähetys epäonnistui. Yritä uudelleen.', 'OK', { duration: 5000 })
        this.lahetetaan.set(false)
        return
      }
      this._dialogRef.close(true)
    } catch {
      this._snackbar.open('Palautteen lähetys epäonnistui. Yritä uudelleen.', 'OK', { duration: 5000 })
      this.lahetetaan.set(false)
    }
  }

  private _muodostaPyynto(palauteTeksti: string): KirjausehdotusPalauteLahetysPyynto {
    const kirjaus = this.data.naytettavaKirjaus.kirjaus
    const ehdotus = kirjaus.ehdotus
    const rivit = this.data.naytettavaKirjaus.naytettavatKirjausrivit ?? []
    const ehdotetutRivit = rivit.filter(rivi => rivi.kirjausrivi?.c !== null && rivi.kirjausrivi?.c !== undefined && !!rivi.tilinumero)
    const ehdotetutTilit = ehdotetutRivit.map(rivi => rivi.tilinumero)
    const ehdotetutAlv = ehdotetutRivit.map(rivi => rivi.alv ?? '')
    return {
      asiakasAvain: this.data.asiakas.avain,
      asiakasNimi: this.data.asiakas.nimi,
      kirjausAvain: kirjaus.avain,
      kirjausnumero: kirjaus.kirjausnumero ?? null,
      kirjausPvm: kirjaus.p ? this._dateService.muotoile(this._dateService.numberToDate(kirjaus.p), 'yyyy-MM-dd') : null,
      palauteTeksti: palauteTeksti,
      confidenceLevel: ehdotus?.confidenceLevel ?? null,
      perustelu: ehdotus?.perustelu ?? null,
      malli: ehdotus?.malli ?? null,
      promptTunniste: ehdotus?.promptTunniste ?? null,
      ehdotetutTilit: ehdotetutTilit.length > 0 ? ehdotetutTilit : null,
      ehdotetutAlv: ehdotetutAlv.length > 0 ? ehdotetutAlv : null,
    }
  }
}
